/**
 * @file Map.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Monday, 8th July 2019 1:40:46 pm
 * @copyright 2015 - 2019 SKAT LLC, Delive LLC
 */
import type { Point, iLogger } from '../../types'
import type { Waypoint } from 'skat-js/types'

import * as React from 'react'
import { getBounds, computeDestinationPoint } from 'geolib'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import { Map as MapComponent } from 'pigeon-maps'
import { Inject } from '../../serviceLocator'
import geoViewport from '@mapbox/geo-viewport'
import { AutoSizer } from 'react-virtualized'
import moize from 'moize'
import { Stage } from 'react-konva'
import axios from 'axios'
import autoBind from 'react-autobind'
import { __ } from 'web-panel/globals'

import Regions from './Regions'
import Drivers from './Drivers'

export type MapState = {
  zoom: number,
  width: number,
  height: number,
  x: number,
  y: number
}

export type LatLngToPixel = ([number, number]) => [number, number]

export type Props = {
  center: [number, number],
  zoom: number,
  waypoints: Array<Waypoint & {_regionDetected: boolean}>,
  route: Array<Point>
}

export type State = {
  defaultPosition: Point,
  defaultZoomLevel: number
}

const POINT_ZOOM = 17

const Line = ({ mapState: { width, height }, latLngToPixel, coordsArray, style = { stroke: '#325f79', strokeWidth: 4 } }) => {
  if (coordsArray.length < 2) return null
  const lines = []
  let pixel = latLngToPixel(coordsArray[0])
  for (let i = 1; i < coordsArray.length; i++) {
    const pixel2 = latLngToPixel(coordsArray[i])
    lines.push(<line markerEnd='url(#head)' key={i} x1={pixel[0]} y1={pixel[1]} x2={pixel2[0]} y2={pixel2[1]} style={style} />)
    pixel = pixel2
  }
  return (
    <svg width={width} height={height} style={{ top: 0, left: 0 }}>
      <defs>
        <marker
          id='head' orient='auto' markerWidth='2' markerHeight='4'
          refX='0.1' refY='2'
        >
          <path d='M0,0 V4 L2,2 Z' fill='#f8a638' />
        </marker>
      </defs>
      {lines}
    </svg>
  )
}

const PointMarker = ({ anchor, left, top, name, details }) => {
  return (
    <div className='point-marker' style={{ left, top }}>
      <div className='pin' data-name={name} />
      <div className='details'>{details}</div>
      <div className='pulse' />
    </div>
  )
}

const RadiusOverlay = ({ center, radius, point, latLngToPixel, name, details }) => {
  const [latitude, longitude] = center
  const [left, top] = latLngToPixel([latitude, longitude])

  const edgePoint = computeDestinationPoint({ latitude, longitude }, radius, 180)
  const [, edgeTop] = latLngToPixel([edgePoint.latitude, edgePoint.longitude])
  const radiusSize = edgeTop - top
  return (
    <div className='radius-overlay' style={{ left: left - radiusSize, top: top - radiusSize, width: radiusSize * 2, height: radiusSize * 2 }} />
  )
}

const ViewportedCanvas = (props) => {
  const { mapState, left, top, drivers, regions, latLngToPixel, isAnimating } = props
  const { width, height, zoom, bounds } = mapState
  const viewport = { minX: left - width / 2, minY: top - height / 2, maxX: left + width / 2, maxY: top + height / 2 }

  return (
    <Stage width={width} height={height}>
      <Regions viewport={viewport} latLngToPixel={latLngToPixel} zoom={zoom} regions={regions} />
      <Drivers bounds={bounds} latLngToPixel={latLngToPixel} zoom={zoom} drivers={drivers} isAnimating={isAnimating} />
    </Stage>
  )
}

const getTileUrl : () => Promise<?string> = moize(async () => {
  const { data } = await axios.get('/map/settings.json')
  const tileEndpoint = data.OSM_TILES
  return tileEndpoint
}, { isPromise: true })

class Map extends React.Component<Props, State> {
  @Inject logger : iLogger

  _isAnimating : boolean = false

  constructor (props: Props) {
    super(props)
    autoBind(this)
    this.state = {
      tileEndpoint: '//geo.cloudtaxi.ru/osm_tiles/{z}/{x}/{y}.png',
      defaultPosition: [0, 0],
      defaultZoomLevel: 13
    }
  }

  async componentDidMount () {
    try {
      const tileEndpoint = await getTileUrl()
      if (tileEndpoint) this.setState({ tileEndpoint })
    } catch (e) {
      this.logger.warn('Unable to load map settings')
    }
  }

  shouldComponentUpdate (props: Props, state: State) {
    if (!isEqual(this.props.waypoints, props.waypoints)) return true
    if (!isEqual(this.props.route, props.route)) return true
    if (!isEqual(this.props.center, props.center)) return true
    if (!isEqual(this.props.drivers, props.drivers)) return true
    if (!isEqual(this.props.size, props.size)) return true
    if (!isEqual(this.props.regions, props.regions)) return true
    if (!isEqual(this.props.radius, props.radius)) return true
    if (!isEqual(this.props.radiusPoint, props.radiusPoint)) return true
    if (this.props.zoom !== props.zoom) return true
    return false
  }

  getTile (x: number, y: number, z: number, reverseY: number) : string {
    const tileUrl = this.state.tileEndpoint
      .replace('{z}', z)
      .replace('{y}', y)
      .replace('{-y}', reverseY)
      .replace('{x}', x)

    return tileUrl
  }

  getBounds = moize(getBounds)
  getWaypointsViewport = moize((waypoints, width, height) => {
    let position, zoom

    waypoints = waypoints.filter(({ latitude, longitude }) => (!isNil(latitude) && !isNil(longitude)))
    if (waypoints.length === 1) {
      const [{ latitude, longitude }] = waypoints
      if (latitude && longitude) position = [latitude, longitude]
      zoom = POINT_ZOOM
    } if (waypoints.length > 1) {
      const { maxLat, maxLng, minLat, minLng } = this.getBounds(waypoints)
      if (maxLat === minLat && maxLng === minLng) {
        position = [minLat, minLng]
        zoom = POINT_ZOOM
      } else {
        const viewport = geoViewport.viewport([minLng, minLat, maxLng, maxLat], [width, height])
        zoom = viewport.zoom - 1
        position = [viewport.center[1], viewport.center[0]]
      }
    }
    return { waypointsZoom: zoom, waypointsPosition: position }
  }, { isDeepEqual: true })

  render () : React.Node {
    const { defaultPosition, defaultZoomLevel } = this.state
    const { drivers = [], waypoints = [], route = [], radiusPoint, radius, center, zoom, regions = [], onClick } = this.props

    return (
      <AutoSizer>{({ width, height }) => {
        const { waypointsZoom, waypointsPosition } = this.getWaypointsViewport(waypoints, width, height)

        return (
          <MapComponent
            attributionPrefix={false}
            attribution='.'
            center={waypointsPosition || center || defaultPosition}
            zoom={radius ? zoom : waypointsZoom || zoom || defaultZoomLevel} // FIXME: Слёзы
            onClick={onClick}
            maxZoom={18}
            animations
            onAnimationStart={radius ? null : () => { this._isAnimating = true }}
            onAnimationStop={radius ? null : () => { this._isAnimating = false }}
            animateMaxScreens={15}
            provider={this.getTile}
            width={width}
            height={height}
            boxClassname='map'
          >
            {waypoints.map(({ name, street, house, latitude, longitude, pointName }, index) => {
              if (!latitude) return null

              if (!pointName) {
                if (index === 0) pointName = 'A'
                else if (index + 1 === waypoints.length) pointName = 'B'
                else pointName = index
              }

              const details = name || [street, house].join(' ').trim() || __('UNKNOWN_ADDRESS')
              return (<PointMarker name={pointName} details={details} key={index} anchor={[latitude, longitude]} payload={1} />)
            })}

            <Line coordsArray={route} />
            <ViewportedCanvas regions={regions} drivers={drivers} isAnimating={this._isAnimating} />
            {radiusPoint ? <RadiusOverlay center={radiusPoint} radius={radius} /> : null}
          </MapComponent>)
      }}
      </AutoSizer>
    )
  }
}

export default Map
